import './coapplicantEmploymentStatusPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { NO, YES, YesOrNo } from '../../../store/applicantStore/types/YesOrNo';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    isCoapplicantCurrentlyEmployed,
    clearCoapplicantCurrentEmployment,
    setIsCoapplicantCurrentlyEmployed,
  } = useCoapplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      isCurrentlyEmployedValue: isCoapplicantCurrentlyEmployed,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<YesOrNo>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }
    const isCurrentlyEmployedValue = event.target.value;

    setIsInitialSelection(
      isEmpty(isCoapplicantCurrentlyEmployed) && isValidClickEvent(event),
    );

    setIsCoapplicantCurrentlyEmployed(isCurrentlyEmployedValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { isCurrentlyEmployedValue } = values;

    if (isCurrentlyEmployedValue) {
      if (isDirty) {
        clearCoapplicantCurrentEmployment();
      }

      navigate(next());
    }
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-employment-status-page" sx={theme.pageLayout}>
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantEmploymentStatus.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantEmploymentStatus.form.h1}
              >
                {t('CoapplicantEmploymentStatusPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantEmploymentStatus.form.h2}
              >
                {t('CoapplicantEmploymentStatusPage.subtitle')}
              </h2>

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  labelPlacement="below"
                  name="isCurrentlyEmployedValue"
                  options={[
                    {
                      label: t('CoapplicantEmploymentStatusPage.yes'),
                      value: YES,
                      testId:
                        locator.pages.coapplicantEmploymentStatus.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantEmploymentStatusPage.no'),
                      value: NO,
                      testId:
                        locator.pages.coapplicantEmploymentStatus.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantEmploymentStatus.form
                      .radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantEmploymentStatus.form.submitButton
                  }
                  disabled={isEmpty(isCoapplicantCurrentlyEmployed)}
                  fullWidth
                  submit
                >
                  {t('CoapplicantEmploymentStatusPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantEmploymentStatus.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEmploymentStatusPage';
